import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslocoService } from '@jsverse/transloco';
import * as marked from 'marked';

import { AppSharedModule } from 'app/app-shared.module';
import { AlertService } from 'app/services/alert.service';
import {
  ChatMessage,
  ChatMessagePart,
  ChatSessionWithName,
  SpotlightAgentChatService,
} from 'app/services/spotlight-agent-chat.service';
import { ChartData } from '../ai-chart/ai-chart.component';
import {
  normalizeDocumentsData,
  normalizeSmartSummaryData,
  RICH_TOOL_NAMES,
  ToolContent,
  toolNameToContentType,
} from '../tool-content.model';

interface HistoryMessage {
  readonly role: 'user' | 'assistant' | 'tool';
  readonly text: string;
  readonly mode: 'ask';
  readonly content: SafeHtml;
  readonly chartData?: ChartData;
  readonly chartToolCallId?: string;
  readonly chartToolName?: string;
  readonly toolContent?: ToolContent;
  readonly toolContentToolCallId?: string;
  readonly toolContentToolName?: string;
}

/**
 * Chat history panel for selecting, renaming, and deleting assistant sessions.
 */
@Component({
  selector: 'app-spotlight-assistant-chat-history-panel',
  templateUrl: './spotlight-assistant-chat-history-panel.component.html',
  imports: [AppSharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpotlightAssistantChatHistoryPanelComponent {
  private readonly spotlightAgentChatService = inject(SpotlightAgentChatService);
  private readonly alertService = inject(AlertService);
  private readonly translocoService = inject(TranslocoService);
  private readonly sanitizer = inject(DomSanitizer);
  protected readonly t = (key: string, params?: Record<string, unknown>): string =>
    this.translocoService.translate(`ai-assistant.${key}`, params);

  readonly sessions = input.required<ChatSessionWithName[]>();
  readonly currentSessionId = input<string | null>(null);
  readonly projectId = input.required<string>();
  readonly collectionId = input<string | null>(null);
  readonly filterByDataSource = input<boolean>(true);

  readonly panelClosed = output<void>();
  readonly messagesLoaded = output<HistoryMessage[]>();
  readonly currentSessionChanged = output<string | null>();
  readonly sessionsUpdated = output<ChatSessionWithName[]>();
  readonly currentSessionDeleted = output<void>();
  readonly filterByDataSourceToggled = output<void>();
  readonly dataSourceSwitched = output<string>();

  protected readonly activeHistorySession = signal<ChatSessionWithName | null>(null);
  protected readonly editingSessionId = signal<string | null>(null);
  protected readonly editingSessionName = signal<string>('');

  protected close(): void {
    this.panelClosed.emit();
  }

  protected async onHistoryItemClick(session: ChatSessionWithName, event: Event): Promise<void> {
    if (this.editingSessionId() === session.id) {
      event.stopPropagation();
      return;
    }
    await this.selectChatSession(session);
  }

  protected openHistorySessionMenu(session: ChatSessionWithName, event: Event): void {
    event.stopPropagation();
    this.activeHistorySession.set(session);
  }

  protected clearActiveHistorySession(): void {
    this.activeHistorySession.set(null);
  }

  protected startRenameSession(): void {
    const session = this.activeHistorySession();
    if (!session) {
      return;
    }
    this.editingSessionId.set(session.id);
    this.editingSessionName.set(session.name ?? '');
    this.clearActiveHistorySession();
  }

  protected cancelRenameSession(): void {
    this.editingSessionId.set(null);
    this.editingSessionName.set('');
  }

  protected updateEditingSessionName(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) {
      return;
    }
    this.editingSessionName.set(target.value);
  }

  protected async submitRenameSession(session: ChatSessionWithName): Promise<void> {
    const nextName = this.editingSessionName().trim();
    if (!nextName || nextName === session.name) {
      this.cancelRenameSession();
      return;
    }
    const renamed = await this.spotlightAgentChatService.renameChatSession(session.id, nextName);
    if (!renamed) {
      this.alertService.showToast(
        this.translocoService.translate('ai-assistant.rename-session-failure'),
        5000,
        'error'
      );
      return;
    }
    await this.reloadSessions();
    this.alertService.showToast(
      this.translocoService.translate('ai-assistant.rename-session-success')
    );
    this.cancelRenameSession();
  }

  protected async onRenameKeydown(
    event: KeyboardEvent,
    session: ChatSessionWithName
  ): Promise<void> {
    if (event.key === 'Enter') {
      event.preventDefault();
      await this.submitRenameSession(session);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelRenameSession();
    }
  }

  protected async onRenameBlur(session: ChatSessionWithName): Promise<void> {
    if (this.editingSessionId() !== session.id) {
      return;
    }
    await this.submitRenameSession(session);
  }

  protected async deleteActiveChatSession(event: Event): Promise<void> {
    const session = this.activeHistorySession();
    if (!session) {
      return;
    }
    event.stopPropagation();
    const deleted = await this.spotlightAgentChatService.deleteChatSession(session.id);
    if (!deleted) {
      return;
    }
    this.alertService.showToast(
      this.translocoService.translate('ai-assistant.delete-session-success')
    );
    const wasCurrent = this.currentSessionId() === session.id;
    await this.reloadSessions();
    this.clearActiveHistorySession();
    if (wasCurrent) {
      this.currentSessionChanged.emit(null);
      this.currentSessionDeleted.emit();
    }
  }

  private async selectChatSession(session: ChatSessionWithName): Promise<void> {
    const loaded = await this.spotlightAgentChatService.loadChatSession(session.id);
    if (!loaded?.messages) {
      this.panelClosed.emit();
      return;
    }
    if (loaded.collectionId && loaded.collectionId !== this.collectionId()) {
      this.dataSourceSwitched.emit(loaded.collectionId);
    }
    const processedMessages = (
      await Promise.all(loaded.messages.map((message) => this.buildHistoryMessages(message)))
    ).flat();
    if (!this.hasRichToolParts(loaded.messages)) {
      const toolResults = await this.spotlightAgentChatService.getToolResults(session.id);
      processedMessages.push(
        ...toolResults
          .map((result) => {
            const normalizedOutput = this.normalizeToolOutput(result.toolName, result.output);
            if (normalizedOutput === null) {
              return null;
            }
            return this.createToolHistoryMessage({
              type: 'dynamic-tool',
              state: 'output-available',
              toolName: result.toolName,
              toolCallId: result.toolCallId,
              output: normalizedOutput,
            });
          })
          .filter((message): message is HistoryMessage => message !== null)
      );
    }
    this.messagesLoaded.emit(processedMessages);
    this.currentSessionChanged.emit(session.id);
    this.panelClosed.emit();
  }

  private async buildHistoryMessages(message: ChatMessage): Promise<HistoryMessage[]> {
    const parts =
      Array.isArray(message.parts) && message.parts.length > 0
        ? message.parts
        : [{ type: 'text', text: message.content } satisfies ChatMessagePart];
    const hasDisplayTablePart = parts.some(
      (part) =>
        part.type === 'dynamic-tool' &&
        part.state === 'output-available' &&
        part.toolName === 'displayTable'
    );
    const historyMessages: HistoryMessage[] = [];
    let bufferedText = '';
    for (const part of parts) {
      if (part.type === 'text') {
        bufferedText += typeof part.text === 'string' ? part.text : '';
        continue;
      }
      if (
        message.role === 'assistant' &&
        part.type === 'dynamic-tool' &&
        part.state === 'output-available'
      ) {
        const trimmedBufferedText = bufferedText.trim();
        if (trimmedBufferedText.length > 0) {
          historyMessages.push(
            await this.createTextHistoryMessage(message.role, bufferedText, hasDisplayTablePart)
          );
          bufferedText = '';
        }
        const toolMessage = this.createToolHistoryMessage(part);
        if (toolMessage) {
          historyMessages.push(toolMessage);
        }
      }
    }
    if (bufferedText.trim().length > 0 || historyMessages.length === 0) {
      historyMessages.push(
        await this.createTextHistoryMessage(
          message.role,
          bufferedText || message.content,
          hasDisplayTablePart
        )
      );
    }
    return historyMessages;
  }

  private hasRichToolParts(messages: ChatMessage[]): boolean {
    return messages.some((message) =>
      (message.parts ?? []).some(
        (part) =>
          part.type === 'dynamic-tool' &&
          part.state === 'output-available' &&
          !!part.toolName &&
          RICH_TOOL_NAMES.has(part.toolName)
      )
    );
  }

  private async createTextHistoryMessage(
    role: 'user' | 'assistant' | 'tool',
    text: string,
    stripMarkdownTables: boolean
  ): Promise<HistoryMessage> {
    const textWithoutImages = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, '');
    const sanitizedText = stripMarkdownTables
      ? this.stripMarkdownTables(textWithoutImages).trim()
      : textWithoutImages.trim();
    const content = this.sanitizer.bypassSecurityTrustHtml(await marked.parse(sanitizedText));
    return {
      role,
      text,
      mode: 'ask',
      content,
    };
  }

  private createToolHistoryMessage(part: ChatMessagePart): HistoryMessage | null {
    if (!part.toolName || part.output == null) {
      return null;
    }
    const chartData = this.normalizeChartOutput(part.output);
    const emptyContent = this.sanitizer.bypassSecurityTrustHtml('');
    if (chartData) {
      return {
        role: 'assistant',
        text: chartData.config.title || 'Chart',
        mode: 'ask',
        content: emptyContent,
        chartData,
        chartToolCallId: part.toolCallId,
        chartToolName: part.toolName,
      };
    }
    const contentType = toolNameToContentType(part.toolName);
    if (!contentType) {
      return null;
    }
    const normalizedOutput = this.normalizeToolOutput(part.toolName, part.output);
    if (normalizedOutput === null) {
      return null;
    }
    return {
      role: 'assistant',
      text: '',
      mode: 'ask',
      content: emptyContent,
      toolContent: { type: contentType, data: normalizedOutput } as ToolContent,
      toolContentToolCallId: part.toolCallId,
      toolContentToolName: part.toolName,
    };
  }

  private stripMarkdownTables(text: string): string {
    return text.replace(/(?:^|\n)\|[^\n]+\|\s*\n\|[\s:|-]+\|\s*\n(?:\|[^\n]+\|\s*\n?)*/g, '\n');
  }

  private normalizeToolOutput(toolName: string, rawOutput: unknown): unknown {
    if (toolName === 'displayChart') {
      return this.normalizeChartOutput(rawOutput);
    }
    if (!rawOutput) {
      return null;
    }
    if (typeof rawOutput === 'string') {
      try {
        return this.normalizeToolOutput(toolName, JSON.parse(rawOutput));
      } catch {
        return null;
      }
    }
    if (typeof rawOutput === 'object') {
      const container = rawOutput as { output?: unknown; result?: unknown };
      const normalizedOutput = container.output ?? container.result ?? rawOutput;
      if (toolName === 'displayDocuments') {
        return normalizeDocumentsData(normalizedOutput);
      }
      if (toolName === 'displaySmartSummary') {
        return normalizeSmartSummaryData(normalizedOutput);
      }
      return normalizedOutput;
    }
    return null;
  }

  private normalizeChartOutput(rawOutput: unknown): ChartData | null {
    if (!rawOutput) {
      return null;
    }
    if (typeof rawOutput === 'string') {
      try {
        const parsed = JSON.parse(rawOutput as string) as ChartData;
        return parsed.config && parsed.data ? parsed : null;
      } catch {
        return null;
      }
    }
    if (typeof rawOutput === 'object') {
      return this.extractChartOutput(rawOutput);
    }
    return null;
  }

  private extractChartOutput(candidate: unknown, depth: number = 0): ChartData | null {
    if (depth > 5 || !candidate) {
      return null;
    }
    if (Array.isArray(candidate)) {
      for (const item of candidate) {
        const nested = this.extractChartOutput(item, depth + 1);
        if (nested) {
          return nested;
        }
      }
      return null;
    }
    if (typeof candidate === 'object') {
      const c = candidate as {
        config?: unknown;
        data?: unknown;
        output?: unknown;
        result?: unknown;
      };
      if (c.config && Array.isArray(c.data)) {
        return candidate as ChartData;
      }
      return (
        this.extractChartOutput(c.output, depth + 1) ?? this.extractChartOutput(c.result, depth + 1)
      );
    }
    return null;
  }

  protected onToggleFilter(): void {
    this.filterByDataSourceToggled.emit();
  }

  private async reloadSessions(): Promise<void> {
    const effectiveCollectionId = this.filterByDataSource() ? this.collectionId() : undefined;
    const sessions = await this.spotlightAgentChatService.getChatSessions(
      this.projectId(),
      effectiveCollectionId
    );
    this.sessionsUpdated.emit(sessions);
  }
}
